import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";

import { CircularProgress } from "@mui/material";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { CompanySize } from "../../../ManageCompanySize/schema/company-size";

import FormLabel from "../../../ToDo/ManageToDos/components/EditableToDoItem/FormLabel";
import FormTextField from "../../../Account/components/FormTextField";

interface EditModeCompanySizeItemProps {
	isLoading?: boolean;
	data?: CompanySize;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	validationSchema: any;
	setEditing: (value: number) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSubmit: (values: any) => void;
}

export default function EditModeCompanySizeItem({
	data,
	validationSchema,
	setEditing,
	onSubmit,
	isLoading,
}: EditModeCompanySizeItemProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-company-size.${key}`);
	const {
		register,
		formState: { errors },
		handleSubmit,
	} = useForm({
		defaultValues: data,
		resolver: zodResolver(validationSchema),
	});

	return (
		<form
			className="w-full rounded-xl p-4 bg-white"
			style={{ boxShadow: "0px 1px 2px 0px #1C274C1F" }}
		>
			{isLoading ? (
				<div className="h-96 flex items-center justify-center">
					<CircularProgress className="!w-14 !h-14" />
				</div>
			) : (
				<>
					<div className="grid grid-cols-8 gap-y-5">
						<FormLabel className="mt-4" title={ts("form-fields.value")} />
						<FormTextField
							autoComplete="name"
							containerClassName="col-span-7 !mb-0"
							errorMessage={errors?.value?.message}
							formRegister={register("value")}
							label={ts("form-fields.value")}
							showLabel={false}
						/>
					</div>

					<div className="flex items-center justify-end gap-2 mt-6">
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION_SECONDARY}
							title={t("basics.cancel")}
							type="button"
							onClick={() => {
								setEditing(-1);
							}}
						/>
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION}
							disabled={isLoading}
							isLoading={isLoading}
							title={data?.id ? t("basics.save") : t("basics.add")}
							type="button"
							onClick={handleSubmit(onSubmit)}
						/>
					</div>
				</>
			)}
		</form>
	);
}
